import React, { useMemo, useRef, useState } from 'react';
import { useI18n } from '../../i18n/index.jsx';
import { pickLang } from '../../content.jsx';
import { usePageMeta } from '../../catalogue.js';
import { useTeam, teamDefaults, flagUrl, countryName, openEmail, displayPhoneIntl, telHref } from '../../team.js';
import TeamLevels from './TeamLevels.jsx';
import RichText from '../../components/RichText.jsx';
import { textOf, typoProps, useTypoFonts } from '../../richText.js';
import MediaBanner, { bannerOf, hasBanner, hasContent } from '../../components/MediaBanner.jsx';
import './team.css';

// /equipe (FR) and /team (EN): people by level (Direction → Team), for the
// whole company or per office (Admin > Website content > Team > Layout).
// Offices without visible members aren't shown (nothing invented).

export function OfficeHeader({ o, lang, preview = false }) {
  const { t } = useI18n();
  const flag = flagUrl(o.country);
  const place = [o.city, countryName(o.country, lang)].filter(Boolean).join(', ');
  const hasEmail = preview ? !!o.email : !!o.emailEnc;
  return (
    <header className="team-office-head">
      {flag ? <img className="team-flag" src={flag} alt={countryName(o.country, lang)} loading="lazy" width="36" height="27" /> : <span className="team-flag is-empty" aria-hidden="true" />}
      <div>
        <h2>{pickLang(o.name, lang)}</h2>
        {place && <p className="muted">{place}</p>}
        {(o.address || o.phone || hasEmail) && (
          <p className="team-office-contact small">
            {o.address && <span className="pre-line">{o.address}</span>}
            {o.phone && <a href={telHref(o.phone)}>{displayPhoneIntl(o.phone)}</a>}
            {hasEmail && <a href="#contact" onClick={(e) => { e.preventDefault(); if (!preview) openEmail(o.emailEnc); }}>{t('team.writeOffice')}</a>}
          </p>
        )}
      </div>
    </header>
  );
}

export default function Team() {
  const { t, lang } = useI18n();
  const { page } = useTeam();
  const doc = page || teamDefaults();
  const [filter, setFilter] = useState('all');
  const top = useRef(null);
  const offices = useMemo(() => (doc.offices || []).filter((o) => !o.hidden && (o.members || []).some((m) => !m.hidden)), [doc]);
  const countries = useMemo(() => [...new Set(offices.map((o) => o.country).filter(Boolean))], [offices]);
  const title = pickLang(doc.title, lang) || t('team.title');
  const intro = pickLang(doc.intro, lang);
  usePageMeta(title, textOf(intro).slice(0, 300) || t('team.metaDescription'));
  const typo = doc.typo || null;
  useTypoFonts(typo);
  const tp = typoProps(typo);
  const pick = (c) => { setFilter(c); top.current?.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' }); };

  const banner = bannerOf(doc);
  // Banner content (eyebrow, title, buttons…) — its title is the page's h1.
  // Older banners could only show the page title + intro (`text`).
  const rich = hasBanner(banner) && hasContent(banner.content);
  const ownTitle = rich && !!pickLang(banner.content.title, lang);
  const onBanner = hasBanner(banner) && !rich && banner.text;
  const heading = (
    <>
      <h1 className="typo-title">{title}</h1>
      {intro && <RichText className="team-intro" value={intro} />}
    </>
  );
  return (
    <div className={`team-page ${tp.className}`} style={tp.style}>
      {hasBanner(banner) && (
        <MediaBanner media={banner} className={`team-banner${onBanner ? ' has-text' : ''}`} titleTag={ownTitle ? 'h1' : 'h2'}>
          {onBanner && <div className="container team-banner-text">{heading}</div>}
        </MediaBanner>
      )}
      <div className="container">
        {!onBanner && !ownTitle && <header className="team-head">{heading}</header>}
        {ownTitle && intro && <header className="team-head"><RichText className="team-intro" value={intro} /></header>}
        {onBanner && intro && <RichText className="team-intro team-intro-below" value={intro} />}
        {countries.length > 1 && (
          <div className="team-chips" role="toolbar" aria-label={t('team.filter')}>
            <button type="button" className={filter === 'all' ? 'on' : ''} aria-pressed={filter === 'all'} onClick={() => pick('all')}>{t('team.all')}</button>
            {countries.map((c) => (
              <button key={c} type="button" className={filter === c ? 'on' : ''} aria-pressed={filter === c} onClick={() => pick(c)}>
                {flagUrl(c) && <img src={flagUrl(c)} alt="" width="20" height="15" />}{countryName(c, lang)}
              </button>
            ))}
          </div>
        )}
        <div ref={top} className="team-offices">
          <TeamLevels doc={doc} lang={lang} filter={filter} officeHeader={(o) => <OfficeHeader o={o} lang={lang} />} />
        </div>
      </div>
    </div>
  );
}
